import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus } from "lucide-react";
import type { ViewCtx } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import { C } from "../../core/schema";
import { Button, Input } from "../../ui";
import { bumpCounter, createCounter, dayKey, valuesOf, type Counter } from "./ops";
import { useSession } from "../../app/session";

/**
 * Counters as tiles (spec §9.7, SP feature): today's value with − and +. No streaks (§9.19). The *tiles* display
 * of the *Habits and counts* widget; every counter shows here and in the grid display.
 */
export function CounterTiles({ ctx, counterIds }: { ctx: ViewCtx; counterIds?: string[] }) {
  const me = useSession((st) => st.session?.profile?.id) ?? null;
  const { t } = useTranslation();
  const counters =
    useDocSelector(ctx.handle, (doc) => doc.getMap(C.counters).toJSON() as Record<string, Counter>) ?? {};
  const [title, setTitle] = useState("");
  const today = dayKey(new Date(ctx.now));
  // archived ones keep their history but leave every list
  const ids = (counterIds?.length ? counterIds.filter((i) => counters[i]) : Object.keys(counters)).filter(
    (i) => !counters[i].archivedAt,
  );
  return (
    <div className="kss-surface flex h-full flex-col gap-2 overflow-auto p-3" data-testid="counters-view">
      {ids.length === 0 && <p className="text-[12.5px] text-neutral-500">{t("counters.empty")}</p>}
      {/* one square tile per counter: label in the top half, the number in the next quarter,
          − and + side by side in the bottom quarter */}
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-2">
        {ids.map((id) => (
          <li
            key={id}
            className="relative flex aspect-square flex-col rounded-md border border-neutral-200 bg-[var(--surface-2)] p-2 dark:border-neutral-800"
            data-testid="counter-row"
          >
            <span className="flex h-1/2 items-center justify-center overflow-hidden text-center text-sm leading-snug">
              <span className="line-clamp-3 break-words">{counters[id].title}</span>
            </span>
            <span className="flex h-1/4 items-baseline justify-center gap-1">
              <span className="font-mono text-[1.6em] leading-none tabular-nums" data-testid="counter-value">
                {valuesOf(counters[id], me)[today] ?? 0}
              </span>
              {counters[id].unit && <span className="text-[12px] text-neutral-500">{counters[id].unit}</span>}
            </span>
            <span className="flex h-1/4 items-end gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 px-0"
                aria-label={t("counters.minus", { title: counters[id].title })}
                disabled={ctx.readOnly}
                onClick={() => ctx.write((doc) => bumpCounter(doc, id, -1))}
              >
                <Minus size={14} aria-hidden />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 px-0"
                aria-label={t("counters.plus", { title: counters[id].title })}
                disabled={ctx.readOnly}
                onClick={() => ctx.write((doc) => bumpCounter(doc, id, 1))}
              >
                <Plus size={14} aria-hidden />
              </Button>
            </span>
          </li>
        ))}
      </ul>
      {!ctx.readOnly && (
        <form
          className="mt-auto flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!title.trim()) return;
            ctx.write((doc) => createCounter(doc, title.trim(), "", { habit: false }));
            setTitle("");
          }}
        >
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("counters.add")}
            aria-label={t("counters.add")}
            compact
            data-testid="counter-input"
          />
        </form>
      )}
    </div>
  );
}
